import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Calendar } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { INSIGHTS, INSIGHT_CATEGORIES } from "../data/insights";

const fmtDate = (s) => new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const Insights = () => {
  const [cat, setCat] = useState("All");
  const filtered = useMemo(() => cat === "All" ? INSIGHTS : INSIGHTS.filter(i => i.category === cat), [cat]);
  const [featured, ...rest] = filtered;

  return (
    <div data-testid="insights-page">
      <PageHeader
        eyebrow="Insights & Perspectives"
        title={<>Boardroom <em className="text-brand-green font-serif italic">thinking.</em> Delivered.</>}
        subtitle="Research-led perspectives from our consulting practice — strategy, operations, digital, ESG, risk and human capital."
      />

      {/* Category Filters */}
      <section className="py-10 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-2">
            {INSIGHT_CATEGORIES.map(c => (
              <button
                key={c}
                data-testid={`category-${c.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 text-sm rounded-full border transition-colors ${
                  cat === c
                    ? "bg-brand-ink text-white border-brand-ink"
                    : "bg-white border-black/10 text-brand-slate hover:border-brand-green hover:text-brand-green"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured + Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {featured && (
            <Link to={`/insights/${featured.slug}`} data-testid={`insight-featured-${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-center">
              <div className="lg:col-span-7 relative aspect-[16/10] rounded-sm overflow-hidden">
                <img src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4 bg-brand-gold text-brand-ink text-xs font-bold tracking-[0.2em] uppercase px-3 py-1.5">Featured · {featured.category}</div>
              </div>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-4 text-xs text-brand-slate">
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {fmtDate(featured.date)}</span>
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {featured.readTime} min read</span>
                </div>
                <h2 className="font-serif text-3xl md:text-5xl text-brand-ink tracking-tight leading-[1.05] mt-5 group-hover:text-brand-green transition-colors">{featured.title}</h2>
                <p className="mt-5 text-brand-slate text-lg leading-relaxed">{featured.excerpt}</p>
                <div className="inline-flex items-center gap-2 mt-7 text-brand-green font-medium group-hover:gap-3 transition-all">
                  Read perspective <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map(i => (
              <Link key={i.slug} to={`/insights/${i.slug}`} data-testid={`insight-${i.slug}`} className="group bg-white border border-black/5 rounded-sm overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all">
                <div className="aspect-[5/3] relative overflow-hidden">
                  <img src={i.cover} alt={i.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-brand-green text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1">{i.category}</div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-brand-slate">
                    <span>{fmtDate(i.date)}</span>
                    <span>·</span>
                    <span>{i.readTime} min</span>
                  </div>
                  <h3 className="font-serif text-xl text-brand-ink mt-3 leading-snug group-hover:text-brand-green transition-colors">{i.title}</h3>
                  <p className="mt-3 text-sm text-brand-slate leading-relaxed line-clamp-3">{i.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-brand-slate">No insights in this category yet — check back soon.</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
